import { useFetchData } from "../../../hooks/useFetch";
import { ApiConfig, API_TRANSPORT_VEHICLE_ROUTER } from "../../../services/api-config";

/**
 * Trạng thái LƯU TRONG DB chỉ có 2. "Đang vận chuyển" là trạng thái suy ra từ `is_in_transit`
 * (xe có chuyến đang mở) — không chọn được ở form, chỉ để hiển thị.
 */
export enum TRANSPORT_VEHICLE_STATUS {
  ACTIVE = 1,
  SUSPENDED = 2,
}

export const TRANSPORT_VEHICLE_STATUS_LABEL: Record<number, string> = {
  [TRANSPORT_VEHICLE_STATUS.ACTIVE]: "Hoạt động",
  [TRANSPORT_VEHICLE_STATUS.SUSPENDED]: "Tạm ngưng",
};

export interface TransportVehicle {
  id: number;
  name: string;
  plate_number: string;
  area: string | null;
  description: string | null;
  status: TRANSPORT_VEHICLE_STATUS;
  /** Có chuyến đang mở — hiển thị thành trạng thái "Đang vận chuyển" */
  is_in_transit: boolean;
  current_driver_id: number | null;
  current_driver_name: string | null;
  on_board_order_count: number;
  /** Đội hình (xem user/docs/xe-tai-doi-hinh-ky-thuat.md). Xe chưa có tài xế → không dùng được ở luồng nào. */
  driver_id: number | null;
  assistant_id: number | null;
  /** % kg của tài xế; phụ xe = assistant_ratio (= 100 - driver_ratio, 0 khi không có phụ xe) */
  driver_ratio: number;
  assistant_ratio: number;
  driver_name: string | null;
  assistant_name: string | null;
  created_at: string;
  updated_at: string;
}

/** Người chọn được làm tài xế / phụ xe. `vehicle_plate` ≠ null → đang thuộc đội hình xe đó. */
export interface CrewOption {
  id: number;
  name: string;
  phone: string;
  vehicle_id: number | null;
  vehicle_plate: string | null;
}

export interface TransportVehicleFilters {
  status?: number;
  area?: string;
  key_search?: string;
}

export interface TransportVehiclePayload {
  name: string;
  plate_number: string;
  area?: string;
  description?: string;
  status?: number;
  /** null = bỏ trống */
  driver_id?: number | null;
  assistant_id?: number | null;
  driver_ratio?: number;
}

/** Kết quả xem trước chuyển đội hình (`GET :id/transfer-preview`). */
export interface VehicleTransferPreview {
  from: { id: number; name: string; plate_number: string };
  to: { id: number; name: string; plate_number: string };
  crew: {
    driver_id: number;
    driver_name: string;
    assistant_id: number | null;
    assistant_name: string | null;
    driver_ratio: number;
  };
  /** Đơn mở đang dùng xe nguồn; `started` = chiều đó tài xế đã nhận / đang chạy. */
  orders: { id: number; code: string; status: number; legs: { leg: "pickup" | "delivery"; started: boolean }[] }[];
  open_trips: { id: number; direction: number; on_board_count: number }[];
  pending_handovers: number;
}

const TransportVehicleService = {
  transferPreview: () => {
    const { request, loading } = useFetchData.Get<VehicleTransferPreview>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, toVehicleId: number) =>
        request({ endPoint: API_TRANSPORT_VEHICLE_ROUTER.API_TRANSFER_PREVIEW(id), data: { to_vehicle_id: toVehicleId } }),
      loading,
    };
  },

  transfer: () => {
    const { request, loading } = useFetchData.Post<{ orders: number; trips: number }>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, toVehicleId: number) =>
        request({ endPoint: API_TRANSPORT_VEHICLE_ROUTER.API_TRANSFER(id), data: { to_vehicle_id: toVehicleId } }),
      loading,
    };
  },

  crewOptions: () => {
    const { request, loading } = useFetchData.Get<CrewOption[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (keySearch = "") =>
        request({
          endPoint: API_TRANSPORT_VEHICLE_ROUTER.API_CREW_OPTIONS,
          data: keySearch ? { key_search: keySearch } : {},
        }),
      loading,
    };
  },

  list: () => {
    const { request, baseResponse, loading } = useFetchData.Get<TransportVehicle[]>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (params: TransportVehicleFilters = {}) =>
        request({ endPoint: API_TRANSPORT_VEHICLE_ROUTER.API_LIST, data: params }),
      response: baseResponse,
      loading,
    };
  },

  create: () => {
    const { request, baseResponse, loading } = useFetchData.Post<TransportVehicle>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (payload: TransportVehiclePayload) =>
        request({ endPoint: API_TRANSPORT_VEHICLE_ROUTER.API_CREATE, data: payload }),
      response: baseResponse,
      loading,
    };
  },

  update: () => {
    const { request, baseResponse, loading } = useFetchData.Post<TransportVehicle>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number, payload: TransportVehiclePayload) =>
        request({ endPoint: API_TRANSPORT_VEHICLE_ROUTER.API_UPDATE(id), data: payload }),
      response: baseResponse,
      loading,
    };
  },

  remove: () => {
    const { request, baseResponse, loading } = useFetchData.Post<{ deleted: boolean }>({
      projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE,
    });
    return {
      fetch: (id: number) =>
        request({ endPoint: API_TRANSPORT_VEHICLE_ROUTER.API_DELETE(id), data: {} }),
      response: baseResponse,
      loading,
    };
  },
};

export default TransportVehicleService;
